import React, { useEffect, useState, useContext } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import brLocale from 'date-fns/locale/pt-BR';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Button, Grid, TextField, Box, Chip } from '@material-ui/core';
import Typography from "@material-ui/core/Typography";
import { InfoOutlined, Assignment, Person, Queue, Help } from '@material-ui/icons';
import api from '../../services/api';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { makeStyles, useTheme } from "@material-ui/core/styles";
import './button.css';
import { i18n } from '../../translate/i18n';
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    container: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.padding,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(2),
    },
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    infoChip: {
        marginLeft: theme.spacing(1),
        fontSize: '0.7rem',
        height: '20px',
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.contrastText,
    },
    explanation: {
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1),
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        borderLeft: `4px solid ${theme.palette.primary.main}`,
    }
}));

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

export const options = {
    responsive: true,
    plugins: {
        legend: {
            position: 'top',
            display: false,
        },
        title: {
            display: true,
            text: 'Tickets',
            position: 'left',
        },
        tooltip: {
            callbacks: {
                title: function(context) {
                    const name = context[0].label;
                    return name === "Não Atribuídos" ? "Tickets Não Atribuídos" : name;
                },
                label: function(context) {
                    const value = context.raw;
                    const name = context.label;
                    if (name === "Não Atribuídos") {
                        return `${value} ticket(s) em fila de espera ou sem atendente`;
                    } else {
                        return `${value} ticket(s) atribuído(s) a este atendente`;
                    }
                },
                footer: function(context) {
                    return 'Dica: Tickets "Não Atribuídos" são contados no total geral';
                }
            },
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            titleColor: '#fff',
            bodyColor: '#fff',
            footerColor: '#ffeb3b',
            cornerRadius: 8,
            padding: 12,
        },
        datalabels: {
            display: true,
            anchor: 'start',
            offset: -30,
            align: "start",
            color: "#fff",
            textStrokeColor: "#000",
            textStrokeWidth: 2,
            font: {
                size: 20,
                weight: "bold"

            },
        }
    },
};

export const ChatsUser = () => {
    const classes = useStyles();
    const theme = useTheme();
    const [initialDate, setInitialDate] = useState(new Date());
    const [finalDate, setFinalDate] = useState(new Date());
    const [ticketsData, setTicketsData] = useState({ data: [] });
    const { user } = useContext(AuthContext);
    
    // Calcular total de tickets atribuídos (excluindo "Não Atribuídos")
    const totalAssignedTickets = ticketsData?.data?.reduce((sum, item) => {
        // Filtrar apenas usuários reais, não "Não Atribuídos"
        if (item.nome && item.nome !== "Não Atribuídos") {
            return sum + (parseInt(item.quantidade) || 0);
        }
        return sum;
    }, 0) || 0;

    const companyId = user.companyId;

    useEffect(() => {
        if (companyId) {
            handleGetTicketsInformation();
        }
    }, [companyId]);

    const dataCharts = {

        labels: ticketsData && ticketsData?.data.length > 0 && ticketsData?.data.map((item) => item.nome),
        datasets: [
            {
                data: ticketsData?.data.length > 0 && ticketsData?.data.map((item, index) => {
                    return item.quantidade
                }),
                backgroundColor: theme.palette.primary.main,
            },

        ],
    };

    const handleGetTicketsInformation = async () => {
        try {

            const { data } = await api.get(`/dashboard/ticketsUsers?initialDate=${format(initialDate, 'yyyy-MM-dd')}&finalDate=${format(finalDate, 'yyyy-MM-dd')}&companyId=${companyId}`);
            setTicketsData(data);
        } catch (error) {
            toast.error('Erro ao buscar informações dos tickets');
        }
    }

    return (
        <>
            <div className={classes.titleContainer}>
                <Typography component="h2" variant="h6" color="primary" gutterBottom>
                    Tickets Atribuídos por Usuário
                </Typography>
                <Chip 
                    icon={<InfoOutlined />}
                    label={`${totalAssignedTickets} atribuídos`}
                    className={classes.infoChip}
                    size="small"
                />
            </div>

            <Box className={classes.explanation}>
                <Typography variant="caption" style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <Assignment style={{ marginRight: '4px', fontSize: '16px' }} />
                    <strong>Este gráfico mostra apenas tickets atribuídos a atendentes específicos.</strong>
                </Typography>
                <Typography variant="caption" style={{ display: 'flex', alignItems: 'center' }}>
                    <InfoOutlined style={{ marginRight: '4px', fontSize: '16px' }} />
                    Tickets em fila de espera ou não atribuídos não aparecem aqui, mas são contados no gráfico "Total de atendimentos" abaixo.
                </Typography>
            </Box>

            <Grid container spacing={2}>
                <Grid item>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                        <DatePicker
                            value={initialDate}
                            onChange={(newValue) => { setInitialDate(newValue) }}
                            label={i18n.t("dashboard.date.initialDate")}
                            renderInput={(params) => <TextField fullWidth {...params} sx={{ width: '20ch' }} />}

                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                        <DatePicker
                            value={finalDate}
                            onChange={(newValue) => { setFinalDate(newValue) }}
                            label={i18n.t("dashboard.date.finalDate")}
                            renderInput={(params) => <TextField fullWidth {...params} sx={{ width: '20ch' }} />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item>
                    <Button style={{ backgroundColor: theme.palette.primary.main, top: '10px' }} onClick={handleGetTicketsInformation} variant='contained'>Filtrar</Button>
                </Grid>
            </Grid>
            <Bar options={options} data={dataCharts} style={{ maxWidth: '100%', maxHeight: '280px', }} />
        </>
    );
}